'use client';

import { Fragment, type ReactNode } from 'react';
import { Box, Grid, Typography } from '@/primitives';
import { formatDob } from '@/lib/dateTime';
import { useSocietyDateFormat } from '@/hooks/publicSearch/useSocietyDisplayOptions';
import { AnimalSexBadge } from '@/hooks/publicSearch/animal/helpers';
import { StatusCell } from '@/components/publicSearch/resultCell/NameCell';
import { isMemberDetailsLinkable } from '@/components/publicSearch/member/memberDetailsLink';
import { HyperLink } from '@/components/publicSearch/resultCell/HyperLink';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import type { AnimalDetailFieldConfigItem } from '@/hooks/publicSearch/animal/animalEbvGrid';
import Link from '@mui/material/Link';
import {
  displayPlainText,
  isStrippedIlr2LinkPlaceholder,
  parseIlr2Anchor,
  parseIlr2ImgBadge,
  parseMixedIlr2Html,
  parsePlainExternalUrl,
  plainTextForTooltip,
  splitDetailMultilinePlainParts,
} from '@/utils/stripHtmlForTooltip';
import {
  CJER_LACTANET_LABEL,
  LAMBPLAN_LINK_LABEL,
  adColTokenToEbvRawKey,
  buildCjerLactanetUrl,
  buildLambplanUrl,
  parseLambplanLinkToken,
} from '@/utils/ilr2ExternalLinkBuilders';
import { Ilr2StructuredImgCell } from '@/components/publicSearch/Ilr2StructuredImgCell';
import { Ilr2MixedHtmlLabel } from '@/components/publicSearch/Ilr2MixedHtmlLabel';
import { ANIMAL_FIELD_NAME, ANIMAL_FIELD_TYPE } from '@/constants/animalFields';
import {
  ANIMAL_STATUS_TONE,
  getAnimalStatusTone,
} from '@/components/animals/animalStatus';
import { slate } from '@/lib/colours';

/** AD_Col action tokens (ILRX-4429). Internal View UI lands with RPT.AnimalExtra. */
const AD_ACTION_TOKENS = new Set(['proof', 'lactations', 'showresults']);

function isAdActionToken(field: string): boolean {
  const lower = field.trim().toLowerCase();
  return AD_ACTION_TOKENS.has(lower) || lower.startsWith('proof');
}

function ExternalAdLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{
        display: 'block',
        minWidth: 0,
        maxWidth: '100%',
        fontSize: { xs: '14px', md: '16px' },
        fontWeight: 500,
        wordBreak: 'break-word',
      }}
    >
      {label}
    </Link>
  );
}

type AnimalSireOrDam = {
  animalId: number;
  animalName: string | null;
};

type AnimalData = {
  animalId: number;
  animalIdent: string | null;
  animalName: string | null;
  animalStatus: string | null;
  dateOfBirth: string | null;
  registrationStatus: string | null;
  sex: string | null;
  tattoo: string | null;
  sire?: AnimalSireOrDam;
  dam?: AnimalSireOrDam;
  ebvRaw?: Record<string, unknown>;
};

type BreederOwnerInfo = {
  id?: number;
  name?: string;
  detailsAvailFlag?: string | null;
};

type AnimalDetailFieldsProps = {
  config: AnimalDetailFieldConfigItem[];
  animalData: AnimalData;
  animalDisplayName?: string | null;
  breeder?: BreederOwnerInfo;
  owner?: BreederOwnerInfo;
  orgCode: string | undefined;
};

const truncatedLinkCellSx = {
  minWidth: 0,
  maxWidth: '100%',
  fontSize: { xs: '14px', md: '16px' },
  '& a': { display: 'block', minWidth: 0, maxWidth: '100%' },
  '& a span': {
    display: 'block',
    wordBreak: 'break-word',
    maxWidth: '100%',
  },
} as const;

/** Strip i4 `^` line-break markers and trim for blank-label detection. */
export function normalizeDetailFieldLabel(
  label: string | null | undefined,
): string {
  return (label ?? '').replace(/\^/g, ' ').trim();
}

export type DetailFieldRun = {
  /** Display label for the run; empty for blank-label stacks (i4 empty AD_Head). */
  label: string;
  fields: AnimalDetailFieldConfigItem[];
};

/**
 * Group type-18 fields for i4 blank AD_Head parity.
 *
 * i4 renders blank heads as `<strong></strong>` — they do **not** inherit the
 * previous label. Attaching them under the prior labelled field mis-labels
 * marketing rows (e.g. Dairy Sire under empty "Donor Dam:"). Consecutive
 * empty-label fields therefore stack as their own unlabelled cell; each
 * labelled field stays a separate cell.
 */
export function groupDetailFieldsByBlankLabels(
  fields: AnimalDetailFieldConfigItem[],
): DetailFieldRun[] {
  const runs: DetailFieldRun[] = [];
  for (const field of fields) {
    const label = normalizeDetailFieldLabel(field.label);
    if (label !== '') {
      runs.push({ label, fields: [field] });
      continue;
    }
    const last = runs[runs.length - 1];
    if (last && last.label === '') {
      last.fields.push(field);
    } else {
      runs.push({ label: '', fields: [field] });
    }
  }
  return runs;
}

/** Maps config field name to the corresponding data key (for non-standard fields) */
function getFieldValue(
  field: string,
  animalData: AnimalData,
  animalDisplayName?: string | null,
  breeder?: BreederOwnerInfo,
  owner?: BreederOwnerInfo,
): {
  type: (typeof ANIMAL_FIELD_TYPE)[keyof typeof ANIMAL_FIELD_TYPE];
  value: unknown;
} {
  const fieldLower = field.toLowerCase();

  // Handle societyAnimalField1-30
  const societyFieldMatch = field.match(/^SocietyAnimalField(\d+)$/i);
  if (societyFieldMatch && animalData.ebvRaw) {
    const key = `societyAnimalField${societyFieldMatch[1]}`;
    return {
      type: ANIMAL_FIELD_TYPE.TEXT,
      value: animalData.ebvRaw[key] ?? '',
    };
  }

  switch (fieldLower) {
    case ANIMAL_FIELD_NAME.ANIMAL_NAME:
    case ANIMAL_FIELD_NAME.NAME:
      return {
        type: ANIMAL_FIELD_TYPE.TEXT,
        value: animalDisplayName || animalData.animalName,
      };
    case ANIMAL_FIELD_NAME.ANIMAL_IDENT:
      return { type: ANIMAL_FIELD_TYPE.TEXT, value: animalData.animalIdent };
    case ANIMAL_FIELD_NAME.REGISTRATION_STATUS:
      return {
        type: ANIMAL_FIELD_TYPE.REGISTRATION_STATUS,
        value: animalData.registrationStatus,
      };
    case ANIMAL_FIELD_NAME.ANIMAL_STATUS:
      return {
        type: ANIMAL_FIELD_TYPE.ANIMAL_STATUS,
        value: animalData.animalStatus,
      };
    case ANIMAL_FIELD_NAME.DATE_OF_BIRTH:
      return { type: ANIMAL_FIELD_TYPE.DATE, value: animalData.dateOfBirth };
    case ANIMAL_FIELD_NAME.SEX:
    case ANIMAL_FIELD_NAME.GENDER:
      return { type: ANIMAL_FIELD_TYPE.SEX, value: animalData.sex };
    case ANIMAL_FIELD_NAME.SIRE:
      return { type: ANIMAL_FIELD_TYPE.SIRE, value: animalData.sire };
    case ANIMAL_FIELD_NAME.DAM:
      return { type: ANIMAL_FIELD_TYPE.DAM, value: animalData.dam };
    case ANIMAL_FIELD_NAME.BREEDER:
      return { type: ANIMAL_FIELD_TYPE.BREEDER, value: breeder };
    case ANIMAL_FIELD_NAME.OWNER:
    case ANIMAL_FIELD_NAME.CURRENT_OWNER:
      return { type: ANIMAL_FIELD_TYPE.OWNER, value: owner };
    case ANIMAL_FIELD_NAME.TATTOO:
    case ANIMAL_FIELD_NAME.ANIMAL_TATTOO:
      return { type: ANIMAL_FIELD_TYPE.TEXT, value: animalData.tattoo };
    default:
      // Try to get from ebvRaw for other fields
      if (animalData.ebvRaw) {
        // Convert field name to camelCase to match ebvRaw keys
        const camelKey = field.charAt(0).toLowerCase() + field.slice(1);
        if (camelKey in animalData.ebvRaw) {
          return {
            type: ANIMAL_FIELD_TYPE.TEXT,
            value: animalData.ebvRaw[camelKey],
          };
        }
      }
      return { type: ANIMAL_FIELD_TYPE.TEXT, value: '' };
  }
}

function isMultilineTextField(
  field: string,
  animalData: AnimalData,
  animalDisplayName?: string | null,
  breeder?: BreederOwnerInfo,
  owner?: BreederOwnerInfo,
): boolean {
  const { type, value } = getFieldValue(
    field,
    animalData,
    animalDisplayName,
    breeder,
    owner,
  );
  if (type !== ANIMAL_FIELD_TYPE.TEXT || value == null || value === '') {
    return false;
  }
  return splitDetailMultilinePlainParts(String(value)).length > 1;
}

/** Whether a run has any value that would display (mirrors FieldValue empty rules). */
function runHasDisplayableValue(
  run: DetailFieldRun,
  animalData: AnimalData,
  animalDisplayName?: string | null,
  breeder?: BreederOwnerInfo,
  owner?: BreederOwnerInfo,
  orgCode?: string,
): boolean {
  for (const fieldConfig of run.fields) {
    if (isAdActionToken(fieldConfig.field)) {
      const lower = fieldConfig.field.trim().toLowerCase();
      if (
        (lower === 'proof' || lower.startsWith('proof')) &&
        orgCode &&
        animalData.animalId &&
        animalData.ebvRaw?.hasProof === true
      ) {
        return true;
      }
      continue;
    }
    if (parseLambplanLinkToken(fieldConfig.field)) {
      const rawKey = adColTokenToEbvRawKey(
        parseLambplanLinkToken(fieldConfig.field)!,
      );
      const rawId = animalData.ebvRaw?.[rawKey];
      if (buildLambplanUrl(rawId != null ? String(rawId) : null)) return true;
      continue;
    }
    if (
      orgCode?.toUpperCase() === 'CJER' &&
      /^SocietyAnimalField17$/i.test(fieldConfig.field)
    ) {
      if (
        buildCjerLactanetUrl({
          countryField20:
            animalData.ebvRaw?.societyAnimalField20 != null
              ? String(animalData.ebvRaw.societyAnimalField20)
              : null,
          sex: animalData.sex,
          animalIdent: animalData.animalIdent,
        })
      ) {
        return true;
      }
      continue;
    }

    const { type, value } = getFieldValue(
      fieldConfig.field,
      animalData,
      animalDisplayName,
      breeder,
      owner,
    );
    switch (type) {
      case ANIMAL_FIELD_TYPE.DATE:
      case ANIMAL_FIELD_TYPE.SEX:
      case ANIMAL_FIELD_TYPE.ANIMAL_STATUS:
        if (value) return true;
        break;
      case ANIMAL_FIELD_TYPE.REGISTRATION_STATUS:
        if (value && displayPlainText(String(value))) return true;
        break;
      case ANIMAL_FIELD_TYPE.SIRE:
      case ANIMAL_FIELD_TYPE.DAM: {
        const animalValue = value as AnimalSireOrDam | undefined;
        if (animalValue?.animalId) return true;
        break;
      }
      case ANIMAL_FIELD_TYPE.BREEDER:
      case ANIMAL_FIELD_TYPE.OWNER: {
        const memberValue = value as BreederOwnerInfo | undefined;
        if (memberValue?.id && memberValue?.name) return true;
        break;
      }
      case ANIMAL_FIELD_TYPE.TEXT:
      default: {
        if (value == null || value === '') break;
        const asString = String(value);
        if (parseIlr2ImgBadge(asString, null) && orgCode) return true;
        if (parseIlr2Anchor(asString, null)) return true;
        if (parsePlainExternalUrl(asString, null)) return true;
        const plain = displayPlainText(asString);
        if (plain && !isStrippedIlr2LinkPlaceholder(plain)) return true;
        break;
      }
    }
  }
  return false;
}

function FieldValue({
  config,
  animalData,
  animalDisplayName,
  breeder,
  owner,
  orgCode,
}: {
  config: AnimalDetailFieldConfigItem;
  animalData: AnimalData;
  animalDisplayName?: string | null;
  breeder?: BreederOwnerInfo;
  owner?: BreederOwnerInfo;
  orgCode: string | undefined;
}): ReactNode {
  const dateFormat = useSocietyDateFormat();
  // Dispatch order (ILRX-4429): 1 action tokens → 2 synth links → 3 A/B TEXT → else hide.
  // societyWebsite base for relative hrefs is deferred until confirmed on this page.
  const societyWebsite: string | null = null;

  if (isAdActionToken(config.field)) {
    // lactations / showresults deferred; proof → View when AD token present and HasProof.
    const lower = config.field.trim().toLowerCase();
    if (lower === 'proof' || lower.startsWith('proof')) {
      if (!orgCode || !animalData.animalId) return null;
      if (animalData.ebvRaw?.hasProof !== true) return null;
      return (
        <Box sx={truncatedLinkCellSx}>
          <HyperLink
            label="View"
            pathname={`/${orgCode}/${ROUTE_SEGMENT.ANIMALS}/${animalData.animalId}/proof`}
            linkStyle={{ padding: 0, minWidth: 0, borderRadius: 0 }}
          />
        </Box>
      );
    }
    return null;
  }

  const lambplanSource = parseLambplanLinkToken(config.field);
  if (lambplanSource) {
    const rawKey = adColTokenToEbvRawKey(lambplanSource);
    const rawId = animalData.ebvRaw?.[rawKey];
    const href = buildLambplanUrl(rawId != null ? String(rawId) : null);
    if (!href) return null;
    return <ExternalAdLink href={href} label={LAMBPLAN_LINK_LABEL} />;
  }

  if (
    orgCode?.toUpperCase() === 'CJER' &&
    /^SocietyAnimalField17$/i.test(config.field)
  ) {
    const href = buildCjerLactanetUrl({
      countryField20:
        animalData.ebvRaw?.societyAnimalField20 != null
          ? String(animalData.ebvRaw.societyAnimalField20)
          : null,
      sex: animalData.sex,
      animalIdent: animalData.animalIdent,
    });
    if (!href) return null;
    return <ExternalAdLink href={href} label={CJER_LACTANET_LABEL} />;
  }

  const { type, value } = getFieldValue(
    config.field,
    animalData,
    animalDisplayName,
    breeder,
    owner,
  );
  const textRaw =
    type === ANIMAL_FIELD_TYPE.TEXT && value != null && value !== ''
      ? String(value)
      : '';
  const multilineParts = splitDetailMultilinePlainParts(textRaw);

  switch (type) {
    case ANIMAL_FIELD_TYPE.DATE: {
      const dateValue = value as string | null;
      if (!dateValue) return null;
      const formatted = formatDob(dateValue, dateFormat);
      return (
        <Typography
          variant="bodylarge"
          component="span"
          title={plainTextForTooltip(formatted)}
          sx={{
            display: 'block',
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {formatted}
        </Typography>
      );
    }

    case ANIMAL_FIELD_TYPE.SEX: {
      const sexValue = value as string | null;
      if (!sexValue) return null;
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          <AnimalSexBadge sex={sexValue} />
          <Typography
            variant="bodylarge"
            component="span"
            title={plainTextForTooltip(sexValue)}
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {sexValue}
          </Typography>
        </Box>
      );
    }

    case ANIMAL_FIELD_TYPE.REGISTRATION_STATUS: {
      const statusValue = value as string | null;
      if (!statusValue) return null;
      const plainStatus = displayPlainText(statusValue);
      if (!plainStatus) return null;
      return (
        <StatusCell
          label={plainStatus}
          sx={(t) => ({
            backgroundColor: `rgba(from ${
              t.vars?.palette.success.main ?? t.palette.success.main
            } r g b / 0.16)`,
            color: t.vars?.palette.success.dark ?? t.palette.success.dark,
            right: 'auto',
            minWidth: 'min-content',
            px: 1.25,
            py: 0.5,
            textTransform: 'none',
            borderRadius: 1,
          })}
        />
      );
    }

    case ANIMAL_FIELD_TYPE.ANIMAL_STATUS: {
      const statusValue = value as string | null;
      if (!statusValue) return null;
      const isActive =
        getAnimalStatusTone(statusValue) === ANIMAL_STATUS_TONE.SUCCESS;
      return (
        <StatusCell
          label={statusValue}
          sx={(t) => ({
            backgroundColor: `rgba(from ${
              isActive
                ? (t.vars?.palette.success.main ?? t.palette.success.main)
                : slate[500]
            } r g b / 0.16)`,
            color: isActive
              ? (t.vars?.palette.success.dark ?? t.palette.success.dark)
              : (t.vars?.palette.text.secondary ?? t.palette.text.secondary),
            right: 'auto',
            minWidth: 'min-content',
            px: 1.25,
            py: 0.5,
            textTransform: 'none',
            borderRadius: 1,
          })}
        />
      );
    }

    case ANIMAL_FIELD_TYPE.SIRE:
    case ANIMAL_FIELD_TYPE.DAM: {
      const animalValue = value as AnimalSireOrDam | undefined;
      if (!animalValue?.animalId) return null;
      const rawName = animalValue.animalName;
      const fallbackLabel = `ID: ${animalValue.animalId}`;
      return (
        <Box
          sx={truncatedLinkCellSx}
          title={plainTextForTooltip(rawName ?? fallbackLabel)}
        >
          <Ilr2MixedHtmlLabel raw={rawName} societyId={orgCode} nowrap>
            {(plain) => (
              <HyperLink
                label={plain || fallbackLabel}
                pathname={`/${orgCode ?? ''}/${ROUTE_SEGMENT.ANIMALS}/${animalValue.animalId}`}
                linkStyle={{ padding: 0, minWidth: 0, borderRadius: 0 }}
              />
            )}
          </Ilr2MixedHtmlLabel>
        </Box>
      );
    }

    case ANIMAL_FIELD_TYPE.BREEDER:
    case ANIMAL_FIELD_TYPE.OWNER: {
      const memberValue = value as BreederOwnerInfo | undefined;
      if (!memberValue?.id || !memberValue?.name) return null;
      if (memberValue.name === 'DETAILS UNKNOWN') {
        return (
          <Typography
            variant="bodylarge"
            component="span"
            sx={{
              display: 'block',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {memberValue.name}
          </Typography>
        );
      }
      if (!isMemberDetailsLinkable(memberValue.detailsAvailFlag)) {
        return (
          <Typography
            variant="bodylarge"
            component="span"
            title={plainTextForTooltip(memberValue.name)}
            sx={{
              display: 'block',
              color: 'text.secondary',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {memberValue.name}
          </Typography>
        );
      }
      return (
        <Box
          sx={truncatedLinkCellSx}
          title={plainTextForTooltip(memberValue.name)}
        >
          <HyperLink
            label={memberValue.name}
            pathname={`/${orgCode ?? ''}/${ROUTE_SEGMENT.MEMBERS}/${memberValue.id}`}
            linkStyle={{ padding: 0, minWidth: 0, borderRadius: 0 }}
          />
        </Box>
      );
    }

    case ANIMAL_FIELD_TYPE.TEXT:
    default: {
      const textValue = value as string | null | undefined;
      if (!textValue) return null;
      const asString = String(textValue);
      // Mixed name + badge (e.g. HERF Super Sire): same split as the search
      // grid — plain text beside the img. Img-only AD values stay badge-only.
      const mixed = parseMixedIlr2Html(asString);
      if (mixed.imgMarkup && mixed.plainText && orgCode) {
        return (
          <Box
            title={plainTextForTooltip(asString)}
            sx={{
              display: 'block',
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <Ilr2MixedHtmlLabel raw={asString} societyId={orgCode} nowrap>
              {(plain) => (
                <Typography
                  variant="bodylarge"
                  component="span"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {plain}
                </Typography>
              )}
            </Ilr2MixedHtmlLabel>
          </Box>
        );
      }

      // Prefer badge/img before text-link parsing. Truncated ILR2 `<a><img`
      // markup is handled by parseIlr2ImgBadge (src + href attrs only).
      // Never print displayPlainText leftovers (e.g. "align=center b").
      const imgBadge = parseIlr2ImgBadge(asString, societyWebsite);
      if (imgBadge && orgCode) {
        const badge = (
          <Ilr2StructuredImgCell
            value={asString}
            societyId={orgCode}
            size="detail"
          />
        );
        if (imgBadge.href) {
          return (
            <Link
              href={imgBadge.href}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: 'inherit',
              }}
            >
              {badge}
            </Link>
          );
        }
        return badge;
      }

      const anchor = parseIlr2Anchor(asString, societyWebsite);
      if (anchor) {
        return <ExternalAdLink href={anchor.href} label={anchor.label} />;
      }
      const plainUrl = parsePlainExternalUrl(asString, societyWebsite);
      if (plainUrl) {
        return <ExternalAdLink href={plainUrl} label={plainUrl} />;
      }

      if (multilineParts.length > 1) {
        return (
          <Typography
            variant="bodylarge"
            component="span"
            title={multilineParts.join('\n')}
            sx={{
              display: 'block',
              minWidth: 0,
              maxWidth: '100%',
              wordBreak: 'break-word',
            }}
          >
            {multilineParts.map((part, i) => (
              <Fragment key={i}>
                {i > 0 && <br />}
                {part}
              </Fragment>
            ))}
          </Typography>
        );
      }

      const plain = displayPlainText(asString);
      // Hide stripped EPD/EBV placeholders only when the URL was not recoverable.
      if (!plain || isStrippedIlr2LinkPlaceholder(plain)) return null;
      return (
        <Typography
          variant="bodylarge"
          component="span"
          title={plainTextForTooltip(plain)}
          sx={{
            display: 'block',
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {plain}
        </Typography>
      );
    }
  }
}

function DetailFieldRunCell({
  run,
  animalData,
  animalDisplayName,
  breeder,
  owner,
  orgCode,
}: {
  run: DetailFieldRun;
  animalData: AnimalData;
  animalDisplayName?: string | null;
  breeder?: BreederOwnerInfo;
  owner?: BreederOwnerInfo;
  orgCode: string | undefined;
}) {
  if (
    !runHasDisplayableValue(
      run,
      animalData,
      animalDisplayName,
      breeder,
      owner,
      orgCode,
    )
  ) {
    return null;
  }

  const useWideCell = run.fields.some((fieldConfig) =>
    isMultilineTextField(
      fieldConfig.field,
      animalData,
      animalDisplayName,
      breeder,
      owner,
    ),
  );

  return (
    <Grid
      size={useWideCell ? { xs: 12, sm: 12, md: 6 } : { xs: 6, sm: 6, md: 3 }}
      sx={{ minWidth: 0 }}
    >
      {run.label ? (
        <Typography
          variant="bodysmall"
          component="div"
          title={run.label}
          sx={{
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {run.label}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.5,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        {run.fields.map((fieldConfig, index) => (
          <FieldValue
            key={`${fieldConfig.field}-${index}`}
            config={fieldConfig}
            animalData={animalData}
            animalDisplayName={animalDisplayName}
            breeder={breeder}
            owner={owner}
            orgCode={orgCode}
          />
        ))}
      </Box>
    </Grid>
  );
}

/** Default fields when no ANIMAL_DETAIL_FIELDS config exists */
const DEFAULT_DETAIL_FIELDS: AnimalDetailFieldConfigItem[] = [
  { field: 'AnimalName', label: 'Name', display: 1, order: '1' },
  { field: 'AnimalIdent', label: 'Animal Ident', display: 1, order: '2' },
  {
    field: 'RegistrationStatus',
    label: 'Registration Status',
    display: 1,
    order: '3',
  },
  { field: 'AnimalStatus', label: 'Animal Status', display: 1, order: '4' },
  { field: 'DateOfBirth', label: 'Birth date', display: 1, order: '5' },
  { field: 'Sex', label: 'Gender', display: 1, order: '6' },
  { field: 'Sire', label: 'Sire', display: 1, order: '7' },
  { field: 'Dam', label: 'Dam', display: 1, order: '8' },
  { field: 'Breeder', label: 'Breeder', display: 1, order: '9' },
  { field: 'Owner', label: 'Owner', display: 1, order: '10' },
];

export default function AnimalDetailFields({
  config,
  animalData,
  animalDisplayName,
  breeder,
  owner,
  orgCode,
}: AnimalDetailFieldsProps) {
  // Society settings type 18 (ANIMAL_DETAIL_FIELDS) are authoritative. i4/ILR2 societies
  // that omit anim_name from AD_Col get AnimalName prepended in
  // RPT.p_SocietySetting18AnimalDetailFields_Insert. Do not inject Name here — BL societies
  // only show it when it is on their db-search form. No config → DEFAULT_DETAIL_FIELDS.
  const fieldsToRender = config.length > 0 ? config : DEFAULT_DETAIL_FIELDS;
  const runs = groupDetailFieldsByBlankLabels(fieldsToRender);

  return (
    <Grid container spacing={2} sx={{ m: 0 }}>
      {runs.map((run, index) => (
        <DetailFieldRunCell
          key={`${run.label || 'blank'}-${run.fields.map((f) => f.field).join('-')}-${index}`}
          run={run}
          animalData={animalData}
          animalDisplayName={animalDisplayName}
          breeder={breeder}
          owner={owner}
          orgCode={orgCode}
        />
      ))}
    </Grid>
  );
}
